import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wand2, Eye, AlertTriangle, ArrowRight } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 focus-visible:ring-cyan-500";

export default function Generator() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState("");
  const [product, setProduct] = useState("");
  const [template, setTemplate] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const { data: customers } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const { data: templates } = useQuery({ queryKey: ["legal-templates"], queryFn: async () => (await api.get("/legal/templates")).data });
  const selT = (templates || []).find((t) => t.id === template);

  const body = () => ({ template_id: template, version: selT?.current_version, customer_id: customer || null, product_id: product || null, variables: {} });

  const runPreview = async () => {
    setBusy(true);
    try { setPreview((await api.post("/legal/generate/preview", body())).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openPdf = async () => {
    try {
      const res = await api.post("/legal/generate/pdf", body(), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDocx = async () => {
    try {
      const res = await api.post("/legal/generate/docx", body(), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "draft_preview.docx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="legal-generator-page">
      <PageHeader title="Document Generator" subtitle="Select customer, product and template — variables auto-fill from your records. Missing fields are flagged, never silently inserted." />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-1">
          <div>
            <label className="text-xs text-slate-400">1 · Customer</label>
            <Select value={customer} onValueChange={setCustomer}>
              <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="gen-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{(customers || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400">2 · Product / Solution</label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="gen-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
              <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{(products || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400">3 · Template &amp; version</label>
            <Select value={template} onValueChange={setTemplate}>
              <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="gen-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
              <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.current_version})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={runPreview} disabled={busy || !template} className="w-full bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="gen-preview-btn"><Wand2 className="mr-1.5 h-4 w-4" /> Preview</Button>
          {preview && (
            <>
              <Button onClick={openPdf} variant="outline" className="w-full border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid="gen-pdf-btn"><Eye className="mr-1.5 h-4 w-4" /> Open PDF</Button>
              <Button onClick={openDocx} variant="outline" className="w-full border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]" data-testid="gen-docx-btn"><Eye className="mr-1.5 h-4 w-4" /> Download DOCX</Button>
              <Button onClick={() => navigate("/legal/contracts")} variant="outline" className="w-full border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10" data-testid="gen-create-contract-btn">Create Contract from this <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            </>
          )}
        </div>

        <div className="lg:col-span-2">
          {preview?.missing_variables?.length > 0 && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-200" data-testid="missing-variables">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div><strong>{preview.missing_variables.length} unresolved field(s)</strong> — shown as [[name]] in the draft: {preview.missing_variables.join(", ")}</div>
            </div>
          )}
          <SectionTitle className="mb-2">Preview {preview ? `· ${preview.template_name} ${preview.version}` : ""}</SectionTitle>
          <div className="max-h-[70vh] overflow-auto rounded-xl border border-[#1E2D4A] bg-white p-6 text-sm text-slate-900" data-testid="gen-preview-body">
            {preview ? <div dangerouslySetInnerHTML={{ __html: preview.html }} /> : <p className="text-slate-400">Select a template and click Preview.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
